/**
 * Learn-a-language phrase banks. You type the phrase in the target language; the game shows
 * its meaning underneath. Accented letters are accepted with or without the accent.
 */
import type { Lesson } from './lessons.js';

export interface Phrase {
  text: string;
  meaning: string;
}
export interface PhraseSet {
  id: string;
  title: string;
  phrases: Phrase[];
}
export interface Language {
  id: string;
  name: string;
  flag: string;
  sets: PhraseSet[];
}

export const LANGUAGES: Language[] = [
  {
    id: 'es',
    name: 'Spanish',
    flag: '🇪🇸',
    sets: [
      {
        id: 'greetings',
        title: 'Greetings',
        phrases: [
          { text: 'Hola, buenos días.', meaning: 'Hello, good morning.' },
          { text: 'Buenas tardes, señora.', meaning: 'Good afternoon, madam.' },
          { text: 'Me llamo Ana. Mucho gusto.', meaning: 'My name is Ana. Nice to meet you.' },
          { text: 'Cómo estás? Muy bien, gracias.', meaning: 'How are you? Very well, thank you.' },
          { text: 'Hasta luego, nos vemos mañana.', meaning: 'See you later, see you tomorrow.' },
          { text: 'Por favor y gracias.', meaning: 'Please and thank you.' },
        ],
      },
      {
        id: 'basics',
        title: 'Numbers and basics',
        phrases: [
          { text: 'uno, dos, tres, cuatro, cinco', meaning: 'one, two, three, four, five' },
          { text: 'seis, siete, ocho, nueve, diez', meaning: 'six, seven, eight, nine, ten' },
          { text: 'Sí, no, quizás.', meaning: 'Yes, no, maybe.' },
          { text: 'Hoy es lunes y mañana es martes.', meaning: 'Today is Monday and tomorrow is Tuesday.' },
          { text: 'No entiendo. Puede repetir?', meaning: "I don't understand. Can you repeat?" },
          { text: 'Habla usted inglés?', meaning: 'Do you speak English?' },
        ],
      },
      {
        id: 'everyday',
        title: 'Everyday life',
        phrases: [
          { text: 'Tengo hambre y sed.', meaning: 'I am hungry and thirsty.' },
          { text: 'Dónde está el baño?', meaning: 'Where is the bathroom?' },
          { text: 'Qué hora es? Son las tres.', meaning: "What time is it? It's three o'clock." },
          { text: 'Me gusta mucho la música.', meaning: 'I like music a lot.' },
          { text: 'Vivo en una casa pequeña.', meaning: 'I live in a small house.' },
          { text: 'Mi familia es muy grande.', meaning: 'My family is very big.' },
        ],
      },
      {
        id: 'travel',
        title: 'Travel and food',
        phrases: [
          { text: 'Un café con leche, por favor.', meaning: 'A coffee with milk, please.' },
          { text: 'La cuenta, por favor.', meaning: 'The bill, please.' },
          { text: 'Cuánto cuesta este libro?', meaning: 'How much does this book cost?' },
          { text: 'Dónde está la estación de tren?', meaning: 'Where is the train station?' },
          { text: 'Quiero una habitación para dos noches.', meaning: 'I want a room for two nights.' },
          { text: 'El pan está muy rico.', meaning: 'The bread is delicious.' },
        ],
      },
    ],
  },
  {
    id: 'fr',
    name: 'French',
    flag: '🇫🇷',
    sets: [
      {
        id: 'greetings',
        title: 'Greetings',
        phrases: [
          { text: 'Bonjour, comment allez-vous?', meaning: 'Hello, how are you?' },
          { text: 'Je vais bien, merci.', meaning: 'I am well, thank you.' },
          { text: "Je m'appelle Marie. Enchantée.", meaning: 'My name is Marie. Pleased to meet you.' },
          { text: 'Bonsoir et bonne nuit.', meaning: 'Good evening and good night.' },
          { text: 'Au revoir, à demain.', meaning: 'Goodbye, see you tomorrow.' },
          { text: "S'il vous plaît et merci beaucoup.", meaning: 'Please and thank you very much.' },
        ],
      },
      {
        id: 'basics',
        title: 'Numbers and basics',
        phrases: [
          { text: 'un, deux, trois, quatre, cinq', meaning: 'one, two, three, four, five' },
          { text: 'six, sept, huit, neuf, dix', meaning: 'six, seven, eight, nine, ten' },
          { text: 'Oui, non, peut-être.', meaning: 'Yes, no, maybe.' },
          { text: "Aujourd'hui c'est lundi.", meaning: 'Today is Monday.' },
          { text: 'Je ne comprends pas.', meaning: "I don't understand." },
          { text: 'Parlez-vous anglais?', meaning: 'Do you speak English?' },
        ],
      },
      {
        id: 'everyday',
        title: 'Everyday life',
        phrases: [
          { text: "J'ai faim et j'ai soif.", meaning: 'I am hungry and I am thirsty.' },
          { text: 'Où sont les toilettes?', meaning: 'Where is the toilet?' },
          { text: 'Quelle heure est-il? Il est trois heures.', meaning: "What time is it? It's three o'clock." },
          { text: "J'aime beaucoup la musique.", meaning: 'I like music a lot.' },
          { text: "J'habite dans une petite maison.", meaning: 'I live in a small house.' },
          { text: 'Ma famille est très grande.', meaning: 'My family is very big.' },
        ],
      },
      {
        id: 'travel',
        title: 'Travel and food',
        phrases: [
          { text: "Un café au lait, s'il vous plaît.", meaning: 'A coffee with milk, please.' },
          { text: "L'addition, s'il vous plaît.", meaning: 'The bill, please.' },
          { text: 'Combien coûte ce livre?', meaning: 'How much does this book cost?' },
          { text: 'Où est la gare?', meaning: 'Where is the train station?' },
          { text: 'Je voudrais une chambre pour deux nuits.', meaning: 'I would like a room for two nights.' },
          { text: 'Le pain est délicieux.', meaning: 'The bread is delicious.' },
        ],
      },
    ],
  },
  {
    id: 'de',
    name: 'German',
    flag: '🇩🇪',
    sets: [
      {
        id: 'greetings',
        title: 'Greetings',
        phrases: [
          { text: 'Guten Morgen, wie geht es Ihnen?', meaning: 'Good morning, how are you?' },
          { text: 'Mir geht es gut, danke.', meaning: 'I am well, thank you.' },
          { text: 'Ich heiße Anna. Freut mich.', meaning: 'My name is Anna. Pleased to meet you.' },
          { text: 'Guten Abend und gute Nacht.', meaning: 'Good evening and good night.' },
          { text: 'Auf Wiedersehen, bis morgen.', meaning: 'Goodbye, see you tomorrow.' },
          { text: 'Bitte und vielen Dank.', meaning: 'Please and thank you very much.' },
        ],
      },
      {
        id: 'basics',
        title: 'Numbers and basics',
        phrases: [
          { text: 'eins, zwei, drei, vier, fünf', meaning: 'one, two, three, four, five' },
          { text: 'sechs, sieben, acht, neun, zehn', meaning: 'six, seven, eight, nine, ten' },
          { text: 'Ja, nein, vielleicht.', meaning: 'Yes, no, maybe.' },
          { text: 'Heute ist Montag.', meaning: 'Today is Monday.' },
          { text: 'Ich verstehe nicht.', meaning: "I don't understand." },
          { text: 'Sprechen Sie Englisch?', meaning: 'Do you speak English?' },
        ],
      },
      {
        id: 'everyday',
        title: 'Everyday life',
        phrases: [
          { text: 'Ich habe Hunger und Durst.', meaning: 'I am hungry and thirsty.' },
          { text: 'Wo ist die Toilette?', meaning: 'Where is the toilet?' },
          { text: 'Wie spät ist es? Es ist drei Uhr.', meaning: "What time is it? It's three o'clock." },
          { text: 'Ich mag Musik sehr.', meaning: 'I like music a lot.' },
          { text: 'Ich wohne in einem kleinen Haus.', meaning: 'I live in a small house.' },
          { text: 'Meine Familie ist sehr groß.', meaning: 'My family is very big.' },
        ],
      },
      {
        id: 'travel',
        title: 'Travel and food',
        phrases: [
          { text: 'Einen Kaffee mit Milch, bitte.', meaning: 'A coffee with milk, please.' },
          { text: 'Die Rechnung, bitte.', meaning: 'The bill, please.' },
          { text: 'Was kostet dieses Buch?', meaning: 'How much does this book cost?' },
          { text: 'Wo ist der Bahnhof?', meaning: 'Where is the train station?' },
          { text: 'Ich möchte ein Zimmer für zwei Nächte.', meaning: 'I would like a room for two nights.' },
          { text: 'Das Brot ist sehr lecker.', meaning: 'The bread is very tasty.' },
        ],
      },
    ],
  },
  {
    id: 'it',
    name: 'Italian',
    flag: '🇮🇹',
    sets: [
      {
        id: 'greetings',
        title: 'Greetings',
        phrases: [
          { text: 'Buongiorno, come sta?', meaning: 'Good morning, how are you?' },
          { text: 'Sto bene, grazie.', meaning: 'I am well, thank you.' },
          { text: 'Mi chiamo Anna. Piacere.', meaning: 'My name is Anna. Pleased to meet you.' },
          { text: 'Buonasera e buonanotte.', meaning: 'Good evening and good night.' },
          { text: 'Arrivederci, a domani.', meaning: 'Goodbye, see you tomorrow.' },
          { text: 'Per favore e grazie mille.', meaning: 'Please and thanks a lot.' },
        ],
      },
      {
        id: 'basics',
        title: 'Numbers and basics',
        phrases: [
          { text: 'uno, due, tre, quattro, cinque', meaning: 'one, two, three, four, five' },
          { text: 'sei, sette, otto, nove, dieci', meaning: 'six, seven, eight, nine, ten' },
          { text: 'Sì, no, forse.', meaning: 'Yes, no, maybe.' },
          { text: 'Oggi è lunedì.', meaning: 'Today is Monday.' },
          { text: 'Non capisco.', meaning: "I don't understand." },
          { text: 'Parla inglese?', meaning: 'Do you speak English?' },
        ],
      },
      {
        id: 'everyday',
        title: 'Everyday life',
        phrases: [
          { text: 'Ho fame e ho sete.', meaning: 'I am hungry and I am thirsty.' },
          { text: 'Dov\'è il bagno?', meaning: 'Where is the bathroom?' },
          { text: 'Che ore sono? Sono le tre.', meaning: "What time is it? It's three o'clock." },
          { text: 'Mi piace molto la musica.', meaning: 'I like music a lot.' },
          { text: 'Abito in una casa piccola.', meaning: 'I live in a small house.' },
          { text: 'La mia famiglia è molto grande.', meaning: 'My family is very big.' },
        ],
      },
      {
        id: 'travel',
        title: 'Travel and food',
        phrases: [
          { text: 'Un caffè latte, per favore.', meaning: 'A coffee with milk, please.' },
          { text: 'Il conto, per favore.', meaning: 'The bill, please.' },
          { text: 'Quanto costa questo libro?', meaning: 'How much does this book cost?' },
          { text: 'Dov\'è la stazione?', meaning: 'Where is the station?' },
          { text: 'Vorrei una camera per due notti.', meaning: 'I would like a room for two nights.' },
          { text: 'Il pane è buonissimo.', meaning: 'The bread is very good.' },
        ],
      },
    ],
  },
  {
    id: 'pt',
    name: 'Portuguese',
    flag: '🇵🇹',
    sets: [
      {
        id: 'greetings',
        title: 'Greetings',
        phrases: [
          { text: 'Bom dia, como está?', meaning: 'Good morning, how are you?' },
          { text: 'Estou bem, obrigada.', meaning: 'I am well, thank you.' },
          { text: 'Chamo-me Ana. Prazer.', meaning: 'My name is Ana. Pleased to meet you.' },
          { text: 'Boa tarde e boa noite.', meaning: 'Good afternoon and good night.' },
          { text: 'Adeus, até amanhã.', meaning: 'Goodbye, see you tomorrow.' },
          { text: 'Por favor e muito obrigado.', meaning: 'Please and thank you very much.' },
        ],
      },
      {
        id: 'basics',
        title: 'Numbers and basics',
        phrases: [
          { text: 'um, dois, três, quatro, cinco', meaning: 'one, two, three, four, five' },
          { text: 'seis, sete, oito, nove, dez', meaning: 'six, seven, eight, nine, ten' },
          { text: 'Sim, não, talvez.', meaning: 'Yes, no, maybe.' },
          { text: 'Hoje é segunda-feira.', meaning: 'Today is Monday.' },
          { text: 'Não entendo.', meaning: "I don't understand." },
          { text: 'Fala inglês?', meaning: 'Do you speak English?' },
        ],
      },
      {
        id: 'everyday',
        title: 'Everyday life',
        phrases: [
          { text: 'Tenho fome e sede.', meaning: 'I am hungry and thirsty.' },
          { text: 'Onde é a casa de banho?', meaning: 'Where is the bathroom?' },
          { text: 'Que horas são? São três horas.', meaning: "What time is it? It's three o'clock." },
          { text: 'Gosto muito de música.', meaning: 'I like music a lot.' },
          { text: 'Moro numa casa pequena.', meaning: 'I live in a small house.' },
          { text: 'A minha família é muito grande.', meaning: 'My family is very big.' },
        ],
      },
      {
        id: 'travel',
        title: 'Travel and food',
        phrases: [
          { text: 'Um café com leite, por favor.', meaning: 'A coffee with milk, please.' },
          { text: 'A conta, por favor.', meaning: 'The bill, please.' },
          { text: 'Quanto custa este livro?', meaning: 'How much does this book cost?' },
          { text: 'Onde é a estação de comboios?', meaning: 'Where is the train station?' },
          { text: 'Quero um quarto para duas noites.', meaning: 'I want a room for two nights.' },
          { text: 'O pão está muito bom.', meaning: 'The bread is very good.' },
        ],
      },
    ],
  },
  {
    id: 'pl',
    name: 'Polish',
    flag: '🇵🇱',
    sets: [
      {
        id: 'greetings',
        title: 'Greetings',
        phrases: [
          { text: 'Dzień dobry, jak się masz?', meaning: 'Good morning, how are you?' },
          { text: 'Dobrze, dziękuję.', meaning: 'Fine, thank you.' },
          { text: 'Mam na imię Anna. Miło mi.', meaning: 'My name is Anna. Nice to meet you.' },
          { text: 'Dobry wieczór i dobranoc.', meaning: 'Good evening and good night.' },
          { text: 'Do widzenia, do jutra.', meaning: 'Goodbye, see you tomorrow.' },
          { text: 'Proszę i dziękuję bardzo.', meaning: 'Please and thank you very much.' },
        ],
      },
      {
        id: 'basics',
        title: 'Numbers and basics',
        phrases: [
          { text: 'jeden, dwa, trzy, cztery, pięć', meaning: 'one, two, three, four, five' },
          { text: 'sześć, siedem, osiem, dziewięć, dziesięć', meaning: 'six, seven, eight, nine, ten' },
          { text: 'Tak, nie, może.', meaning: 'Yes, no, maybe.' },
          { text: 'Dzisiaj jest poniedziałek.', meaning: 'Today is Monday.' },
          { text: 'Nie rozumiem.', meaning: "I don't understand." },
          { text: 'Czy mówisz po angielsku?', meaning: 'Do you speak English?' },
        ],
      },
      {
        id: 'everyday',
        title: 'Everyday life',
        phrases: [
          { text: 'Jestem głodny i spragniony.', meaning: 'I am hungry and thirsty.' },
          { text: 'Gdzie jest łazienka?', meaning: 'Where is the bathroom?' },
          { text: 'Która godzina? Jest trzecia.', meaning: "What time is it? It's three o'clock." },
          { text: 'Bardzo lubię muzykę.', meaning: 'I like music a lot.' },
          { text: 'Mieszkam w małym domu.', meaning: 'I live in a small house.' },
          { text: 'Moja rodzina jest bardzo duża.', meaning: 'My family is very big.' },
        ],
      },
      {
        id: 'travel',
        title: 'Travel and food',
        phrases: [
          { text: 'Kawa z mlekiem, proszę.', meaning: 'A coffee with milk, please.' },
          { text: 'Rachunek, proszę.', meaning: 'The bill, please.' },
          { text: 'Ile kosztuje ta książka?', meaning: 'How much does this book cost?' },
          { text: 'Gdzie jest dworzec kolejowy?', meaning: 'Where is the train station?' },
          { text: 'Chciałbym pokój na dwie noce.', meaning: 'I would like a room for two nights.' },
          { text: 'Chleb jest bardzo smaczny.', meaning: 'The bread is very tasty.' },
        ],
      },
    ],
  },
];

export function languageById(id: string): Language | undefined {
  return LANGUAGES.find((l) => l.id === id);
}

/** Build lesson objects for a language's phrase sets so the lesson runner can play them. */
export function languageLessons(lang: Language): Lesson[] {
  return lang.sets.map((set) => ({
    id: `lang-${lang.id}-${set.id}`,
    title: `${lang.flag} ${lang.name}: ${set.title}`,
    description: `${set.phrases.length} phrases. Accents are optional: type "e" for "é".`,
    keys: '',
    lines: set.phrases.map((p) => p.text),
    meanings: set.phrases.map((p) => p.meaning),
    accentInsensitive: true,
    targetWpm: 25,
  }));
}
